import { useCallback, useEffect, useRef, useState } from 'react';

import { AI_DISCLAIMER_TEXT } from '../utils/aiDisclaimerConstants';
import {
  buildBotMessageAnnouncementText,
  TRANSCRIPT_NAVIGATION_INSTRUCTIONS,
} from '../utils/announcementText';
import { SENDER_TYPES } from '../utils/senderTypes';

const TYPING_ANNOUNCEMENT = 'VA chatbot is typing';
const TYPING_ANNOUNCEMENT_DELAY_MS = 500;
const MAX_POLITE_ANNOUNCEMENTS = 12;
// Safety bound for broken payloads only. Must never clip real answers.
const MAX_ANNOUNCEMENT_CHARS = 4000;
// How long an answer waits for its feedback prompt to share one announcement.
const FEEDBACK_PROMPT_PAIR_WINDOW_MS = 500;

/**
 * Owns everything screen readers hear in the chat conversation: the polite
 * live-region entries for bot messages and typing, and the assertive entry
 * for errors.
 *
 * Invariants this hook protects:
 * - An answer and its trailing feedback prompt must share a single polite
 *   entry (a separate one makes VoiceOver cut off the answer), so answers
 *   are buffered for FEEDBACK_PROMPT_PAIR_WINDOW_MS before flushing.
 * - Only entries from already-committed renders may be pruned. Screen
 *   readers capture live-region text at the inserting commit, so pruning
 *   committed entries is safe and keeps JAWS's full-region re-read from
 *   finding old ones.
 *
 * Cross-concern wiring (what cancels typing, where focus goes) stays in the
 * caller via the returned primitives.
 */
export default function useChatAnnouncements({
  errorMessage,
  messages,
  showAiDisclaimer,
}) {
  const pendingTypingAnnouncementTimerRef = useRef(null);
  const pendingBotAnnouncementRef = useRef(null);
  const announcedBotMessageIdsRef = useRef(new Set());
  const hasAnnouncedTypingRef = useRef(false);
  const announcementIdRef = useRef(0);
  const stalePoliteAnnouncementIdsRef = useRef(new Set());
  const [politeAnnouncements, setPoliteAnnouncements] = useState([]);
  const [assertiveAnnouncement, setAssertiveAnnouncement] = useState('');

  const clearPendingTypingAnnouncement = useCallback(() => {
    if (pendingTypingAnnouncementTimerRef.current) {
      clearTimeout(pendingTypingAnnouncementTimerRef.current);
      pendingTypingAnnouncementTimerRef.current = null;
    }
  }, []);

  const clearPendingBotAnnouncement = useCallback(() => {
    if (pendingBotAnnouncementRef.current) {
      clearTimeout(pendingBotAnnouncementRef.current.timerId);
      pendingBotAnnouncementRef.current = null;
    }
  }, []);

  const resetTypingAnnounced = useCallback(() => {
    hasAnnouncedTypingRef.current = false;
  }, []);

  const appendPoliteAnnouncement = useCallback(text => {
    const normalizedText = text?.trim();
    if (!normalizedText) {
      return;
    }

    const boundedText =
      normalizedText.length > MAX_ANNOUNCEMENT_CHARS
        ? `${normalizedText.slice(0, MAX_ANNOUNCEMENT_CHARS)}...`
        : normalizedText;

    announcementIdRef.current += 1;
    const id = `sr-${announcementIdRef.current}`;

    // Prune captured entries so JAWS's full-region re-read only finds new ones.
    setPoliteAnnouncements(previousAnnouncements => {
      const staleIds = stalePoliteAnnouncementIdsRef.current;
      const nextAnnouncements = [
        ...previousAnnouncements.filter(
          announcement => !staleIds.has(announcement.id),
        ),
        { id, text: boundedText },
      ];

      return nextAnnouncements.slice(-MAX_POLITE_ANNOUNCEMENTS);
    });
  }, []);

  // Screen readers capture live-region text at the inserting commit, so committed entries are safe to prune.
  useEffect(() => {
    politeAnnouncements.forEach(announcement => {
      stalePoliteAnnouncementIdsRef.current.add(announcement.id);
    });
  }, [politeAnnouncements]);

  const scheduleTypingAnnouncement = useCallback(() => {
    if (
      !hasAnnouncedTypingRef.current &&
      !pendingTypingAnnouncementTimerRef.current
    ) {
      pendingTypingAnnouncementTimerRef.current = setTimeout(() => {
        appendPoliteAnnouncement(TYPING_ANNOUNCEMENT);
        hasAnnouncedTypingRef.current = true;
        pendingTypingAnnouncementTimerRef.current = null;
      }, TYPING_ANNOUNCEMENT_DELAY_MS);
    }
  }, [appendPoliteAnnouncement]);

  // Announces the buffered answer, speaking the feedback prompt text
  // as part of the same entry. No-ops when nothing is buffered.
  const flushPendingBotAnnouncement = useCallback(
    suffix => {
      const pending = pendingBotAnnouncementRef.current;
      if (!pending) {
        return;
      }

      clearTimeout(pending.timerId);
      pendingBotAnnouncementRef.current = null;
      const combinedText = suffix ? `${pending.text} ${suffix}` : pending.text;
      appendPoliteAnnouncement(
        pending.withInstructions
          ? `${combinedText} ${TRANSCRIPT_NAVIGATION_INSTRUCTIONS}`
          : combinedText,
      );
    },
    [appendPoliteAnnouncement],
  );

  // Buffers an answer briefly so a trailing feedback prompt joins the same
  // live-region entry. A separate one would make VoiceOver cut off the answer.
  const stagePendingBotAnnouncement = useCallback(
    (text, { withInstructions = true } = {}) => {
      flushPendingBotAnnouncement();
      const timerId = setTimeout(() => {
        flushPendingBotAnnouncement();
      }, FEEDBACK_PROMPT_PAIR_WINDOW_MS);
      pendingBotAnnouncementRef.current = { text, timerId, withInstructions };
    },
    [flushPendingBotAnnouncement],
  );

  useEffect(() => {
    let hasNewBotMessage = false;
    const hasUserMessage = messages.some(
      message => message.sender === SENDER_TYPES.USER,
    );

    messages.forEach(message => {
      if (message.sender !== SENDER_TYPES.BOT) {
        return;
      }

      if (announcedBotMessageIdsRef.current.has(message.id)) {
        return;
      }

      announcedBotMessageIdsRef.current.add(message.id);
      hasNewBotMessage = true;

      // Announce the feedback prompt with its buffered answer; if the answer
      // already announced (late prompt), skip it rather than interrupt.
      if (message.isFeedback) {
        flushPendingBotAnnouncement(
          buildBotMessageAnnouncementText(message, {
            includeSpeakerPrefix: false,
          }),
        );
        return;
      }

      // Messages with options move focus which already announces the message.
      // This prevents it from reading twice.
      if (message.options?.length) {
        return;
      }

      const announcementText = buildBotMessageAnnouncementText(message, {
        disclaimerText: showAiDisclaimer ? AI_DISCLAIMER_TEXT : '',
      });
      if (announcementText) {
        stagePendingBotAnnouncement(announcementText, {
          withInstructions:
            hasUserMessage && !message.feedbackAcknowledgementTargetMessageId,
        });
      }
    });

    if (hasNewBotMessage) {
      clearPendingTypingAnnouncement();
      hasAnnouncedTypingRef.current = false;
    }
  }, [
    clearPendingTypingAnnouncement,
    flushPendingBotAnnouncement,
    messages,
    showAiDisclaimer,
    stagePendingBotAnnouncement,
  ]);

  useEffect(() => {
    if (!errorMessage) {
      setAssertiveAnnouncement('');
      return;
    }

    clearPendingTypingAnnouncement();
    clearPendingBotAnnouncement();
    hasAnnouncedTypingRef.current = false;
    setAssertiveAnnouncement(errorMessage);
  }, [
    clearPendingBotAnnouncement,
    clearPendingTypingAnnouncement,
    errorMessage,
  ]);

  useEffect(() => {
    return () => {
      clearPendingTypingAnnouncement();
      clearPendingBotAnnouncement();
    };
  }, [clearPendingBotAnnouncement, clearPendingTypingAnnouncement]);

  return {
    assertiveAnnouncement,
    clearPendingBotAnnouncement,
    clearPendingTypingAnnouncement,
    politeAnnouncements,
    resetTypingAnnounced,
    scheduleTypingAnnouncement,
  };
}
